import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '@/components/Layout';
import { TourCard } from '@/components/TourCard';
import { TourFilters } from '@/components/TourFilters';
import { useTours } from '@/hooks/useTours';
import { Loader2, Shield, Users, Star } from 'lucide-react';
import toursHeroBg from '@/assets/tours-hero-bg.jpg';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Tours = () => {
  const [filters, setFilters] = useState<any>({});
  const { tours, loading } = useTours();

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const filteredTours = tours.filter((tour) => {
    if (filters.category && tour.category_id !== filters.category) return false;
    if (filters.minPrice && tour.adult_price < filters.minPrice) return false;
    if (filters.maxPrice && tour.adult_price > filters.maxPrice) return false;
    return true;
  });

  const featuredTours = filteredTours.filter((tour) => tour.is_featured);

  return (
    <Layout>
      <Helmet>
        <title>Tours & Excursions - Discover Dominican Republic</title>
        <meta
          name="description"
          content="Explore our collection of amazing tours and excursions in Dominican Republic. From beach adventures to cultural experiences, find your perfect tour."
        />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative min-h-[500px] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${toursHeroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Discover Amazing Tours
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-8">
            Experience the best of Dominican Republic with our carefully curated tours and excursions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="#tours">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-8">
                Browse Tours
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 text-lg px-8"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="bg-primary text-primary-foreground py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <Shield className="w-8 h-8" />
              <h3 className="font-semibold">Fully Insured</h3>
              <p className="text-sm opacity-90">Complete safety coverage</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Users className="w-8 h-8" />
              <h3 className="font-semibold">Expert Guides</h3>
              <p className="text-sm opacity-90">Professional local guides</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Star className="w-8 h-8" />
              <h3 className="font-semibold">5-Star Service</h3>
              <p className="text-sm opacity-90">Exceptional experiences</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16" id="tours">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filters Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                <TourFilters onFiltersChange={handleFiltersChange} />
              </div>
            </aside>

            {/* Tours Grid */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="flex justify-center items-center min-h-[400px]">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Featured Tours */}
                  {featuredTours.length > 0 && (
                    <div className="mb-12">
                      <div className="mb-6">
                        <h2 className="text-3xl font-bold mb-2">Featured Tours</h2>
                        <p className="text-muted-foreground">
                          Our most popular and highly-rated experiences
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {featuredTours.map((tour) => (
                          <TourCard key={tour.id} tour={tour} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All Tours */}
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h2 className="text-3xl font-bold mb-2">
                          {featuredTours.length > 0 ? 'All Tours' : 'Available Tours'}
                        </h2>
                        <p className="text-muted-foreground">
                          {filteredTours.length} {filteredTours.length === 1 ? 'tour' : 'tours'} available
                        </p>
                      </div>
                    </div>

                    {filteredTours.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filteredTours.map((tour) => (
                          <TourCard key={tour.id} tour={tour} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-xl text-muted-foreground">
                          No tours found matching your criteria
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-primary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready for Your Adventure?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Book your tour today and let us create an unforgettable experience tailored to your preferences.
              All tours include professional guides, transportation, and our commitment to excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="text-lg px-8">
                  Contact Us Today
                </Button>
              </Link>
              <Link to="/transfers">
                <Button variant="outline" size="lg" className="text-lg px-8">
                  View Transfers
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Tours;